/**
 * Portfolio Total Value Hook
 *
 * Fetches total USD value of all wallet holdings (SOL + SPL tokens)
 * using Helius DAS API via getWalletHoldings.
 * Lightweight wrapper for use in WalletButton dropdown.
 */

import { useQuery } from '@tanstack/react-query';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import { getWalletHoldings } from '@/lib/portfolio/portfolioClient';
import { queryKeys } from '@/lib/queryClient';
import { formatUsd } from '@/hooks/useSOLPrice';

export function usePortfolioValue() {
  const { walletAddress, connected } = useTurnkeyWallet();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.wallet.portfolioValue(walletAddress ?? ''),
    queryFn: async () => {
      if (!walletAddress) return null;
      const holdings = await getWalletHoldings(walletAddress);
      return holdings;
    },
    enabled: connected && !!walletAddress,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    refetchInterval: 30_000,
  });

  const totalValueUsd = data?.totalValueUsd ?? null;
  const formattedTotalUsd = totalValueUsd !== null ? formatUsd(totalValueUsd) : null;

  return {
    totalValueUsd,
    formattedTotalUsd,
    isLoading,
    error: error ? String(error) : null,
  };
}
